import React, { useState } from 'react';
import { AccountPageLayout } from 'pages/Account/components';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Box,
  Button,
  Select,
  SelectOptionInterface,
} from '@clariness/clarikit-react';
import { Services } from 'services';
import { Store } from 'store';
import { changeUrlLocale } from 'utils/routing';
import { captureEventV2 } from 'utils/events/captureEvent';
import { Events } from '@clariness/tracking-event-runtime';

export const AccountLanguage = () => {
  const intl = useIntl();

  const [pickedLanguageCode, setPickedLanguageCode] = useState<string>();

  const { languages } = Services.SiteEmployees.useGetLanguages();

  const { user } = Store.useUser();

  const languageOptions: SelectOptionInterface[] = languages.map(
    (language) => ({
      id: language.code,
      label: language.name,
      value: language.code,
    })
  );

  const userLanguageOption =
    languageOptions.find((option) => option.value === user?.languageCode) ??
    null;

  const selectedLanguageCode = pickedLanguageCode ?? userLanguageOption?.value;

  const { updateCurrentSiteEmployee, isLoading } =
    Services.SiteEmployees.useUpdateCurrentSiteEmployee({
      onSuccess: (updatedAccount) => {
        captureEventV2(
          Events.InvestigatorService.InvestigatorChangeLanguage({
            InvestigatorID: user?.id ?? '',
          })
        );
        changeUrlLocale(updatedAccount.languageCode);
      },
    });

  return (
    <AccountPageLayout>
      <AccountPageLayout.Heading>
        <FormattedMessage
          id="is.account_language.heading"
          defaultMessage="Language"
        />
      </AccountPageLayout.Heading>

      <AccountPageLayout.Paragraph>
        <FormattedMessage
          id="is.account_language.main_paragraph"
          defaultMessage="You can update your language settings here."
        />
      </AccountPageLayout.Paragraph>

      <AccountPageLayout.Form
        onSubmit={() =>
          updateCurrentSiteEmployee({ languageCode: selectedLanguageCode })
        }
      >
        <Box width="270px" maxWidth="100%">
          {languageOptions.length > 0 && (
            <Select
              key={userLanguageOption?.id ?? 'no-language'}
              id="account-language-select"
              label={intl.formatMessage({
                id: 'is.account_language.heading',
                defaultMessage: 'Language',
              })}
              dropDownOptionList={languageOptions}
              defaultSelectedValue={userLanguageOption}
              onChange={(option) => setPickedLanguageCode(option?.value)}
              data-test-id="account-language-select"
            />
          )}
        </Box>

        <Box>
          <Button
            submit
            disabled={
              !selectedLanguageCode ||
              selectedLanguageCode === user?.languageCode
            }
            loading={isLoading}
            data-test-id="language-save-button"
          >
            {intl.formatMessage({
              id: 'is.account_language.save_language_button',
              defaultMessage: 'Save',
            })}
          </Button>
        </Box>
      </AccountPageLayout.Form>
    </AccountPageLayout>
  );
};
